import { useState, useEffect } from "react";
import { Search, Plus, Edit2, Trash2, Loader2, X, CheckCircle, AlertCircle, ChevronDown, ChevronUp, Users, RotateCcw, MapPin, Eye } from "lucide-react";
import { adminService, Client, Unidade, ClientUser } from "../../../services/adminService";

// ── Helpers de máscara ──────────────────────────────────────────────────────────
const maskCNPJ = (v: string) => {
  const n = v.replace(/\D/g, '').slice(0, 14);
  return n
    .replace(/(\d{2})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1/$2')
    .replace(/(\d{4})(\d{1,2})$/, '$1-$2');
};

const maskPhone = (v: string) => {
  const n = v.replace(/\D/g, '').slice(0, 11);
  if (n.length <= 6) return n.replace(/(\d{2})(\d{0,4})/, '($1) $2');
  if (n.length <= 10) return n.replace(/(\d{2})(\d{4})(\d{0,4})/, '($1) $2-$3');
  return n.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
};

const maskCEP = (v: string) => {
  const n = v.replace(/\D/g, '').slice(0, 8);
  return n.replace(/(\d{5})(\d{1,3})/, '$1-$2');
};

// ── Estado inicial do formulário ─────────────────────────────────────────────
const formDataVazio = {
  razaoSocial: '', nomeFantasia: '', cnpj: '',
  inscricaoEstadual: '', inscricaoMunicipal: '', segmento: '', codigoInterno: '',
  prazoVencimentoDias: 30,
  email: '', password: '', telefone: '', celular: '', site: '',
  contatoNome: '', contatoTelefone: '', contatoEmail: '',
  cep: '', logradouro: '', numero: '', complemento: '', bairro: '', cidade: '', estado: '',
  observacoes: '',
};

type FormData = typeof formDataVazio;

export function ClientManagement() {
  const [clients, setClients] = useState<Client[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [formTab, setFormTab] = useState<"geral" | "endereco" | "acesso">("geral");
  const [cnpjLoading, setCnpjLoading] = useState(false);
  const [cepLoading, setCepLoading] = useState(false);
  const [showUnidadeModal, setShowUnidadeModal] = useState(false);
  const [editingClient, setEditingClient] = useState<Client | null>(null);
  const [editingUnidade, setEditingUnidade] = useState<Unidade | null>(null);
  const [selectedClientId, setSelectedClientId] = useState<string | null>(null);
  const [expandedClient, setExpandedClient] = useState<string | null>(null);
  const [expandedTab, setExpandedTab] = useState<"unidades" | "usuarios">("unidades");
  const [clientUnidades, setClientUnidades] = useState<Unidade[]>([]);
  const [clientUsers, setClientUsers] = useState<ClientUser[]>([]);
  const [showUserModal, setShowUserModal] = useState(false);
  const [editingUser, setEditingUser] = useState<ClientUser | null>(null);
  const [message, setMessage] = useState<{ type: "success" | "error"; text: string } | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [viewingClient, setViewingClient] = useState<Client | null>(null);

  const [formData, setFormData] = useState<FormData>(formDataVazio);

  const [unidadeForm, setUnidadeForm] = useState({
    nome: "", cnpj: "",
    contatoNome: "", contatoTelefone: "", contatoEmail: "",
    cep: "", logradouro: "", numero: "", complemento: "", bairro: "", cidade: "", estado: ""
  });

  const [userForm, setUserForm] = useState({
    name: "", email: "", password: "", role: "visualizador",
    permPmoc: true, permNf: true, permProposta: true
  });

  useEffect(() => { loadClients(); }, [searchTerm]);

  // ── Busca por CEP via ViaCEP ──────────────────────────────────────────────
  const buscarCEP = async (cep: string, setter: (d: Partial<FormData>) => void) => {
    const cepLimpo = cep.replace(/\D/g, '');
    if (cepLimpo.length !== 8) return;
    setCepLoading(true);
    try {
      const resp = await fetch(`https://viacep.com.br/ws/${cepLimpo}/json/`);
      const data = await resp.json();
      if (!data.erro) {
        setter({
          logradouro: data.logradouro || '',
          bairro: data.bairro || '',
          cidade: data.localidade || '',
          estado: data.uf || '',
        });
      }
    } catch { }
    finally { setCepLoading(false); }
  };

  // ── Busca CNPJ via BrasilAPI ──────────────────────────────────────────────
  const buscarCNPJ = async (cnpj: string) => {
    const cnpjLimpo = cnpj.replace(/\D/g, '');
    if (cnpjLimpo.length !== 14) return;
    setCnpjLoading(true);
    try {
      const resp = await fetch(`https://brasilapi.com.br/api/cnpj/v1/${cnpjLimpo}`);
      if (!resp.ok) return;
      const data = await resp.json();
      setFormData(p => ({
        ...p,
        razaoSocial: data.razao_social || p.razaoSocial,
        nomeFantasia: data.nome_fantasia || p.nomeFantasia,
        cep: data.cep?.replace(/\D/g, '') ? maskCEP(data.cep) : p.cep,
        logradouro: data.logradouro || p.logradouro,
        numero: data.numero || p.numero,
        complemento: data.complemento || p.complemento,
        bairro: data.bairro || p.bairro,
        cidade: data.municipio || p.cidade,
        estado: data.uf || p.estado,
        email: data.email || p.email,
      }));
    } catch { }
    finally { setCnpjLoading(false); }
  };

  const set = (field: keyof FormData) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData(p => ({ ...p, [field]: e.target.value }));
  };


  const loadClients = async () => {
    setIsLoading(true);
    try {
      const result = await adminService.listClients(searchTerm);
      setClients(result.data);
    } catch (error) {
      console.error("Erro ao carregar clientes:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const loadUnidades = async (clientId: string) => {
    try {
      const unidades = await adminService.listUnidades(clientId);
      setClientUnidades(unidades);
    } catch (error) {
      console.error("Erro ao carregar unidades:", error);
    }
  };

  const loadClientUsers = async (clientId: string) => {
    try {
      const users = await adminService.listClientUsers(clientId);
      setClientUsers(users);
    } catch (error) {
      console.error("Erro ao carregar usuários:", error);
    }
  };

  const toggleExpand = async (clientId: string) => {
    if (expandedClient === clientId) {
      setExpandedClient(null);
    } else {
      setExpandedClient(clientId);
      setExpandedTab("unidades");
      await loadUnidades(clientId);
      await loadClientUsers(clientId);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);

    // Validação frontend: email obrigatório na criação
    if (!editingClient && !formData.email.trim()) {
      setFormError("E-mail de acesso é obrigatório (aba Acesso ao Portal).");
      setFormTab("acesso");
      return;
    }
    if (!editingClient && !formData.password.trim()) {
      setFormError("Senha é obrigatória (aba Acesso ao Portal).");
      setFormTab("acesso");
      return;
    }

    try {
      if (editingClient) {
        await adminService.updateClient(editingClient.id, formData);
        setMessage({ type: "success", text: "Cliente atualizado com sucesso!" });
      } else {
        await adminService.createClient(formData as any);
        setMessage({ type: "success", text: "Cliente criado com sucesso!" });
      }
      closeModal();
      loadClients();
    } catch (error: any) {
      const errText = error.response?.data?.error || "Erro ao salvar cliente";
      // Se o erro for relacionado a email/senha, vai direto para a aba de acesso
      const isAcessoError = /e-?mail|senha|password/i.test(errText);
      if (isAcessoError) setFormTab("acesso");
      setFormError(errText);
    }
  };

  const handleUnidadeSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedClientId) return;
    try {
      if (editingUnidade) {
        await adminService.updateUnidade(editingUnidade.id, unidadeForm);
        setMessage({ type: "success", text: "Unidade atualizada com sucesso!" });
      } else {
        await adminService.createUnidade(selectedClientId, unidadeForm);
        setMessage({ type: "success", text: "Unidade criada com sucesso!" });
      }
      closeUnidadeModal();
      loadUnidades(selectedClientId);
      loadClients();
    } catch (error: any) {
      setMessage({ type: "error", text: error.response?.data?.error || "Erro ao salvar unidade" });
    }
  };

  const handleEdit = (client: Client) => {
    setEditingClient(client);
    setFormTab("geral");
    setFormData({
      razaoSocial: client.razaoSocial,
      nomeFantasia: client.nomeFantasia || "",
      cnpj: maskCNPJ(client.cnpj || ""),
      inscricaoEstadual: client.inscricaoEstadual || "",
      inscricaoMunicipal: client.inscricaoMunicipal || "",
      segmento: client.segmento || "",
      codigoInterno: client.codigoInterno || "",
      prazoVencimentoDias: client.prazoVencimentoDias ?? 30,
      email: client.email,
      password: "",
      telefone: client.telefone || "",
      celular: client.celular || "",
      site: client.site || "",
      contatoNome: client.contatoNome || "",
      contatoTelefone: client.contatoTelefone || "",
      contatoEmail: client.contatoEmail || "",
      cep: client.cep ? maskCEP(client.cep) : "",
      logradouro: client.logradouro || "",
      numero: client.numero || "",
      complemento: client.complemento || "",
      bairro: client.bairro || "",
      cidade: client.cidade || "",
      estado: client.estado || "",
      observacoes: client.observacoes || "",
    });
    setShowModal(true);
  };


  const handleEditUnidade = (unidade: Unidade, clientId: string) => {
    setEditingUnidade(unidade);
    setSelectedClientId(clientId);
    setUnidadeForm({
      nome: unidade.nome, cnpj: unidade.cnpj || "",
      contatoNome: unidade.contatoNome || "", contatoTelefone: unidade.contatoTelefone || "",
      contatoEmail: unidade.contatoEmail || "", cep: unidade.cep || "",
      logradouro: unidade.logradouro || "", numero: unidade.numero || "",
      complemento: unidade.complemento || "", bairro: unidade.bairro || "",
      cidade: unidade.cidade || "", estado: unidade.estado || ""
    });
    setShowUnidadeModal(true);
  };

  const handleDelete = async (id: string) => {
    if (!confirm("Deseja desativar este cliente?")) return;
    try {
      await adminService.deleteClient(id);
      setMessage({ type: "success", text: "Cliente desativado!" });
      loadClients();
    } catch (error: any) {
      const msg = error.response?.data?.error || "Erro ao desativar cliente";
      setMessage({ type: "error", text: msg });
    }
  };

  const handleReactivate = async (id: string) => {
    if (!confirm("Deseja reativar este cliente?")) return;
    try {
      await adminService.updateClient(id, { active: true });
      setMessage({ type: "success", text: "Cliente reativado com sucesso!" });
      loadClients();
    } catch { setMessage({ type: "error", text: "Erro ao reativar cliente" }); }
  };

  const handleDeleteUnidade = async (id: number) => {
    if (!confirm("Deseja desativar esta unidade?")) return;
    try {
      await adminService.deleteUnidade(id);
      setMessage({ type: "success", text: "Unidade desativada!" });
      if (expandedClient) loadUnidades(expandedClient);
      loadClients();
    } catch { setMessage({ type: "error", text: "Erro ao desativar unidade" }); }
  };

  const openNewModal = () => {
    setEditingClient(null);
    setFormTab("geral");
    setFormData(formDataVazio);
    setShowModal(true);
  };


  const openNewUnidadeModal = (clientId: string) => {
    setEditingUnidade(null);
    setSelectedClientId(clientId);
    setUnidadeForm({
      nome: "", cnpj: "", contatoNome: "", contatoTelefone: "", contatoEmail: "",
      cep: "", logradouro: "", numero: "", complemento: "", bairro: "", cidade: "", estado: ""
    });
    setShowUnidadeModal(true);
  };

  const closeModal = () => { setShowModal(false); setEditingClient(null); setFormError(null); };
  const closeUnidadeModal = () => { setShowUnidadeModal(false); setEditingUnidade(null); };
  const closeUserModal = () => { setShowUserModal(false); setEditingUser(null); };

  const openNewUserModal = (clientId: string) => {
    setEditingUser(null);
    setSelectedClientId(clientId);
    setUserForm({ name: "", email: "", password: "", role: "visualizador", permPmoc: true, permNf: true, permProposta: true });
    setShowUserModal(true);
  };

  const handleEditUser = (user: ClientUser, clientId: string) => {
    setEditingUser(user);
    setSelectedClientId(clientId);
    // Parse permissões do JSON
    const perms = user.tiposPermitidos ? JSON.parse(user.tiposPermitidos) : ["pmoc", "nf", "proposta"];
    setUserForm({
      name: user.name,
      email: user.email,
      password: "",
      role: user.role,
      permPmoc: perms.includes("pmoc"),
      permNf: perms.includes("nf"),
      permProposta: perms.includes("proposta"),
    });
    setShowUserModal(true);
  };

  const handleUserSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedClientId) return;
    try {
      // Montar array de permissões
      const tiposPermitidos: string[] = [];
      if (userForm.permPmoc) tiposPermitidos.push("pmoc");
      if (userForm.permNf) tiposPermitidos.push("nf");
      if (userForm.permProposta) tiposPermitidos.push("proposta");

      const dataToSend = {
        name: userForm.name,
        email: userForm.email,
        password: userForm.password,
        role: userForm.role,
        tiposPermitidos: JSON.stringify(tiposPermitidos),
      };

      if (editingUser) {
        await adminService.updateClientUser(editingUser.id, dataToSend);
        setMessage({ type: "success", text: "Usuário atualizado com sucesso!" });
      } else {
        await adminService.createClientUser(selectedClientId, dataToSend as any);
        setMessage({ type: "success", text: "Usuário criado com sucesso!" });
      }
      closeUserModal();
      loadClientUsers(selectedClientId);
    } catch (error: any) {
      setMessage({ type: "error", text: error.response?.data?.error || "Erro ao salvar usuário" });
    }
  };

  const handleDeleteUser = async (id: string) => {
    if (!confirm("Deseja desativar este usuário?")) return;
    try {
      await adminService.deleteClientUser(id);
      setMessage({ type: "success", text: "Usuário desativado!" });
      if (expandedClient) loadClientUsers(expandedClient);
    } catch { setMessage({ type: "error", text: "Erro ao desativar usuário" }); }
  };

  const clientRoles = [
    { value: "admin_cliente", label: "Administrador" },
    { value: "gerente", label: "Gerente" },
    { value: "visualizador", label: "Visualizador" },
  ];


  return (
    <div className="space-y-6">
      {message && (
        <div className={`p-4 rounded-lg flex items-center gap-3 ${message.type === "success" ? "bg-green-50 text-green-800 border border-green-200" : "bg-red-50 text-red-800 border border-red-200"}`}>
          {message.type === "success" ? <CheckCircle className="w-5 h-5" /> : <AlertCircle className="w-5 h-5" />}
          {message.text}
          <button onClick={() => setMessage(null)} className="ml-auto"><X className="w-4 h-4" /></button>
        </div>
      )}

      <div className="flex flex-col md:flex-row gap-4 justify-between">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input type="text" placeholder="Buscar clientes..." value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-input rounded-lg bg-card text-foreground placeholder:text-muted-foreground" />
        </div>
        <button onClick={openNewModal} className="flex items-center gap-2 px-4 py-2 btn btn-primary">
          <Plus className="w-4 h-4" /> Novo Cliente
        </button>
      </div>

      <div className="bg-card border border-border rounded-lg overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center py-12"><Loader2 className="w-8 h-8 text-brand animate-spin" /></div>
        ) : clients.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">Nenhum cliente encontrado</div>
        ) : (
          <div className="divide-y divide-border">
            {clients.map((client) => (
              <div key={client.id}>
                <div className="flex items-center justify-between p-4 hover:bg-muted/50">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="font-medium text-foreground truncate">{client.razaoSocial}</p>
                      {client.codigoInterno && (
                        <span className="text-xs text-muted-foreground bg-muted px-1.5 py-0.5 rounded">{client.codigoInterno}</span>
                      )}
                    </div>
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 mt-0.5">
                      <span className="text-sm text-muted-foreground">{client.email}</span>
                      <span className="text-sm text-muted-foreground font-mono">{maskCNPJ(client.cnpj)}</span>
                      {(client.cidade || client.estado) && (
                        <span className="text-sm text-muted-foreground flex items-center gap-1">
                          <MapPin className="w-3 h-3" />{client.cidade}{client.cidade && client.estado ? '/' : ''}{client.estado}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-3 ml-4 shrink-0">
                    <div className="hidden md:flex items-center gap-3 text-xs text-muted-foreground">
                      <span title="Unidades">{client._count?.unidades || 0} un.</span>
                      <span title="Propostas">{client._count?.propostas || 0} prop.</span>
                      <span title="Faturas">{client._count?.faturas || 0} fat.</span>
                    </div>
                    <span className={`px-2 py-1 rounded-full text-xs ${client.active ? "bg-green-500/20 text-green-700 dark:text-green-400" : "bg-red-500/20 text-red-700 dark:text-red-400"}`}>
                      {client.active ? "Ativo" : "Inativo"}
                    </span>
                    <div className="flex items-center gap-1">
                      <button onClick={() => toggleExpand(client.id)} className="p-2 text-muted-foreground hover:bg-muted rounded-lg">
                        {expandedClient === client.id ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                      </button>
                      <button onClick={() => setViewingClient(client)} className="p-2 text-muted-foreground hover:bg-muted rounded-lg" title="Visualizar"><Eye className="w-4 h-4" /></button>
                      <button onClick={() => handleEdit(client)} className="p-2 text-blue-600 dark:text-blue-400 hover:bg-blue-500/10 rounded-lg" title="Editar"><Edit2 className="w-4 h-4" /></button>
                      {client.active ? (
                        <button onClick={() => handleDelete(client.id)} className="p-2 text-red-600 dark:text-red-400 hover:bg-red-500/10 rounded-lg" title="Desativar"><Trash2 className="w-4 h-4" /></button>
                      ) : (
                        <button onClick={() => handleReactivate(client.id)} className="p-2 text-green-600 dark:text-green-400 hover:bg-green-500/10 rounded-lg" title="Reativar"><RotateCcw className="w-4 h-4" /></button>
                      )}
                    </div>
                  </div>
                </div>
                {expandedClient === client.id && (
                  <div className="bg-muted/50 p-4 border-t">
                    {/* Abas */}
                    <div className="flex gap-4 mb-4 border-b">
                      <button onClick={() => setExpandedTab("unidades")}
                        className={`pb-2 px-1 text-sm font-medium border-b-2 ${expandedTab === "unidades" ? "border-blue-600 text-blue-600 dark:text-blue-400" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
                        Unidades ({clientUnidades.length})
                      </button>
                      <button onClick={() => setExpandedTab("usuarios")}
                        className={`pb-2 px-1 text-sm font-medium border-b-2 flex items-center gap-1 ${expandedTab === "usuarios" ? "border-blue-600 text-blue-600 dark:text-blue-400" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
                        <Users className="w-4 h-4" /> Usuários ({clientUsers.length})
                      </button>
                    </div>

                    {/* Conteúdo Unidades */}
                    {expandedTab === "unidades" && (
                      <>
                        <div className="flex items-center justify-between mb-3">
                          <h4 className="font-medium text-foreground">Unidades</h4>
                          <button onClick={() => openNewUnidadeModal(client.id)} className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1">
                            <Plus className="w-4 h-4" /> Nova Unidade
                          </button>
                        </div>
                        {clientUnidades.length === 0 ? (
                          <p className="text-sm text-muted-foreground">Nenhuma unidade cadastrada</p>
                        ) : (
                          <div className="space-y-2">
                            {clientUnidades.map((unidade) => (
                              <div key={unidade.id} className="flex items-center justify-between p-3 bg-card rounded-lg border">
                                <div>
                                  <p className="font-medium text-foreground">{unidade.nome}</p>
                                  <p className="text-sm text-muted-foreground">{unidade.cnpj || "Sem CNPJ"} • {unidade.cidade || "Sem cidade"}/{unidade.estado || ""}</p>
                                </div>
                                <div className="flex items-center gap-1">
                                  <button onClick={() => handleEditUnidade(unidade, client.id)} className="p-1 text-blue-600 dark:text-blue-400 hover:bg-blue-500/10 rounded"><Edit2 className="w-4 h-4" /></button>
                                  <button onClick={() => handleDeleteUnidade(unidade.id)} className="p-1 text-red-600 dark:text-red-400 hover:bg-red-500/10 rounded"><Trash2 className="w-4 h-4" /></button>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </>
                    )}

                    {/* Conteúdo Usuários */}
                    {expandedTab === "usuarios" && (
                      <>
                        <div className="flex items-center justify-between mb-3">
                          <h4 className="font-medium text-foreground">Usuários de Acesso</h4>
                          <button onClick={() => openNewUserModal(client.id)} className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1">
                            <Plus className="w-4 h-4" /> Novo Usuário
                          </button>
                        </div>
                        {clientUsers.length === 0 ? (
                          <p className="text-sm text-muted-foreground">Nenhum usuário adicional cadastrado. O cliente pode acessar com o email principal.</p>
                        ) : (
                          <div className="space-y-2">
                            {clientUsers.map((user) => (
                              <div key={user.id} className="flex items-center justify-between p-3 bg-card rounded-lg border">
                                <div>
                                  <p className="font-medium text-foreground">{user.name}</p>
                                  <p className="text-sm text-muted-foreground">{user.email} • {clientRoles.find(r => r.value === user.role)?.label || user.role}</p>
                                </div>
                                <div className="flex items-center gap-2">
                                  <span className={`px-2 py-1 rounded-full text-xs ${user.active ? "bg-green-500/20 text-green-700 dark:text-green-400" : "bg-red-500/20 text-red-700 dark:text-red-400"}`}>
                                    {user.active ? "Ativo" : "Inativo"}
                                  </span>
                                  <button onClick={() => handleEditUser(user, client.id)} className="p-1 text-blue-600 dark:text-blue-400 hover:bg-blue-500/10 rounded"><Edit2 className="w-4 h-4" /></button>
                                  <button onClick={() => handleDeleteUser(user.id)} className="p-1 text-red-600 dark:text-red-400 hover:bg-red-500/10 rounded"><Trash2 className="w-4 h-4" /></button>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>


      {/* Modal Cliente – 3 abas */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-start justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-card rounded-lg w-full max-w-2xl p-6 my-8">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">{editingClient ? "Editar Cliente" : "Novo Cliente"}</h3>
              <button onClick={closeModal}><X className="w-5 h-5" /></button>
            </div>

            {/* Abas do formulário */}
            <div className="flex border-b mb-5">
              {(["geral", "endereco", "acesso"] as const).map((tab) => {
                const labels = { geral: "Dados Gerais", endereco: "Endereço", acesso: "Acesso ao Portal" };
                return (
                  <button key={tab} onClick={() => { setFormTab(tab); setFormError(null); }}
                    className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${formTab === tab
                      ? "border-blue-600 text-blue-600 dark:text-blue-400"
                      : "border-transparent text-muted-foreground hover:text-foreground"
                      }`}>
                    {labels[tab]}
                  </button>
                );
              })}
            </div>

            <form onSubmit={handleSubmit}>
              {/* ── Aba: Dados Gerais ── */}
              {formTab === "geral" && (
                <div className="space-y-4">
                  {/* Linha CNPJ + botão consulta */}
                  <div>
                    <label className="block text-sm font-medium mb-1">CNPJ *
                      {cnpjLoading && <span className="ml-2 text-xs text-muted-foreground">buscando...</span>}
                    </label>
                    <input type="text" value={formData.cnpj} placeholder="00.000.000/0000-00"
                      onChange={(e) => setFormData(p => ({ ...p, cnpj: maskCNPJ(e.target.value) }))}
                      onBlur={() => !editingClient && buscarCNPJ(formData.cnpj)}
                      required className="w-full px-3 py-2 border border-input rounded-lg bg-card text-foreground font-mono" />
                    {!editingClient && (
                      <p className="text-xs text-muted-foreground mt-1">Os dados da empresa serão preenchidos automaticamente ao sair do campo.</p>
                    )}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium mb-1">Razão Social *</label>
                      <input type="text" value={formData.razaoSocial} onChange={set("razaoSocial")} required
                        className="w-full px-3 py-2 border border-input rounded-lg bg-card text-foreground" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Nome Fantasia</label>
                      <input type="text" value={formData.nomeFantasia} onChange={set("nomeFantasia")}
                        className="w-full px-3 py-2 border border-input rounded-lg bg-card text-foreground" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Segmento</label>
                      <select value={formData.segmento} onChange={set("segmento")}
                        className="w-full px-3 py-2 border border-input rounded-lg bg-card text-foreground">
                        <option value="">Selecione...</option>
                        {["Tecnologia", "Saúde", "Varejo", "Indústria", "Construção",
                          "Consultoria", "Alimentação", "Logística", "Serviços Gerais", "Outro"]
                          .map(s => <option key={s} value={s.toLowerCase()}>{s}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Inscrição Estadual</label>
                      <input type="text" value={formData.inscricaoEstadual} onChange={set("inscricaoEstadual")}
                        className="w-full px-3 py-2 border border-input rounded-lg bg-card text-foreground" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Inscrição Municipal</label>
                      <input type="text" value={formData.inscricaoMunicipal} onChange={set("inscricaoMunicipal")}
                        className="w-full px-3 py-2 border border-input rounded-lg bg-card text-foreground" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">
                        Código Interno
                        <span className="ml-2 text-xs text-muted-foreground font-normal">(gerado automaticamente)</span>
                      </label>
                      <input
                        type="text"
                        value={formData.codigoInterno}
                        readOnly
                        placeholder="CLI-0001, CLI-0002..."
                        className="w-full px-3 py-2 border border-input rounded-lg bg-muted text-muted-foreground cursor-not-allowed"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">
                        Prazo de Vencimento (dias)
                        <span className="ml-2 text-xs text-muted-foreground font-normal">(faturas geradas de propostas)</span>
                      </label>
                      <input
                        type="number"
                        min="0"
                        value={formData.prazoVencimentoDias}
                        onChange={(e) => setFormData({ ...formData, prazoVencimentoDias: Number(e.target.value) || 0 })}
                        className="w-full px-3 py-2 border border-input rounded-lg bg-card text-foreground"
                      />
                    </div>
                  </div>

                  <div className="border-t pt-4">
                    <p className="text-sm font-medium mb-3">Contato Responsável</p>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium mb-1">Nome</label>
                        <input type="text" value={formData.contatoNome} onChange={set("contatoNome")}
                          className="w-full px-3 py-2 border border-input rounded-lg bg-card text-foreground" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-1">Telefone da Empresa</label>
                        <input type="text" value={formData.telefone}
                          onChange={(e) => setFormData(p => ({ ...p, telefone: maskPhone(e.target.value) }))}
                          placeholder="(00) 0000-0000"
                          className="w-full px-3 py-2 border border-input rounded-lg bg-card text-foreground" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-1">Celular / WhatsApp</label>
                        <input type="text" value={formData.celular}
                          onChange={(e) => setFormData(p => ({ ...p, celular: maskPhone(e.target.value) }))}
                          placeholder="(00) 00000-0000"
                          className="w-full px-3 py-2 border border-input rounded-lg bg-card text-foreground" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-1">Telefone do Contato</label>
                        <input type="text" value={formData.contatoTelefone}
                          onChange={(e) => setFormData(p => ({ ...p, contatoTelefone: maskPhone(e.target.value) }))}
                          className="w-full px-3 py-2 border border-input rounded-lg bg-card text-foreground" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-1">Email do Contato</label>
                        <input type="email" value={formData.contatoEmail} onChange={set("contatoEmail")}
                          className="w-full px-3 py-2 border border-input rounded-lg bg-card text-foreground" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-1">Site</label>
                        <input type="text" value={formData.site} onChange={set("site")}
                          placeholder="https://" className="w-full px-3 py-2 border border-input rounded-lg bg-card text-foreground" />
                      </div>
                    </div>
                  </div>

                  <div className="border-t pt-4">
                    <label className="block text-sm font-medium mb-1">Observações Internas</label>
                    <textarea value={formData.observacoes} onChange={set("observacoes")} rows={3}
                      placeholder="Notas visibles apenas para a equipe..."
                      className="w-full px-3 py-2 border border-input rounded-lg bg-card text-foreground resize-none" />
                  </div>
                </div>
              )}

              {/* ── Aba: Endereço ── */}
              {formTab === "endereco" && (
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">CEP
                        {cepLoading && <span className="ml-2 text-xs text-muted-foreground">buscando...</span>}
                      </label>
                      <input type="text" value={formData.cep}
                        onChange={(e) => setFormData(p => ({ ...p, cep: maskCEP(e.target.value) }))}
                        onBlur={() => buscarCEP(formData.cep, (d) => setFormData(p => ({ ...p, ...d })))}
                        placeholder="00000-000"
                        className="w-full px-3 py-2 border border-input rounded-lg bg-card text-foreground" />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium mb-1">Logradouro</label>
                      <input type="text" value={formData.logradouro} onChange={set("logradouro")}
                        className="w-full px-3 py-2 border border-input rounded-lg bg-card text-foreground" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Número</label>
                      <input type="text" value={formData.numero} onChange={set("numero")}
                        className="w-full px-3 py-2 border border-input rounded-lg bg-card text-foreground" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Complemento</label>
                      <input type="text" value={formData.complemento} onChange={set("complemento")}
                        className="w-full px-3 py-2 border border-input rounded-lg bg-card text-foreground" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Bairro</label>
                      <input type="text" value={formData.bairro} onChange={set("bairro")}
                        className="w-full px-3 py-2 border border-input rounded-lg bg-card text-foreground" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Cidade</label>
                      <input type="text" value={formData.cidade} onChange={set("cidade")}
                        className="w-full px-3 py-2 border border-input rounded-lg bg-card text-foreground" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">UF</label>
                      <input type="text" value={formData.estado} onChange={set("estado")}
                        maxLength={2} placeholder="SP"
                        className="w-full px-3 py-2 border border-input rounded-lg bg-card text-foreground uppercase" />
                    </div>
                  </div>
                </div>
              )}

              {/* ── Aba: Acesso ao Portal ── */}
              {formTab === "acesso" && (
                <div className="space-y-4">
                  <div className="p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg text-sm text-blue-700 dark:text-blue-300">
                    Este email e senha são usados pelo cliente para acessar o Portal do Cliente.
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Email de Acesso *</label>
                    <input type="email" value={formData.email} onChange={set("email")} required
                      className="w-full px-3 py-2 border border-input rounded-lg bg-card text-foreground" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">
                      Senha {editingClient ? "(deixe vazio para manter a atual)" : "*"}
                    </label>
                    <input type="password" value={formData.password} onChange={set("password")}
                      required={!editingClient} minLength={6}
                      placeholder={editingClient ? "••••••" : "Mínimo 6 caracteres"}
                      className="w-full px-3 py-2 border border-input rounded-lg bg-card text-foreground" />
                  </div>
                </div>
              )}

              {/* Erro do formulário */}
              {formError && (
                <div className="flex items-start gap-2 p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 text-sm mt-4">
                  <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                  <span>{formError}</span>
                  <button type="button" onClick={() => setFormError(null)} className="ml-auto">
                    <X className="w-4 h-4" />
                  </button>
                </div>
              )}

              {/* Botões */}
              <div className="flex gap-3 mt-4 pt-4 border-t">
                <button type="button" onClick={closeModal} className="flex-1 py-2 btn btn-outline">Cancelar</button>
                <button type="submit" className="flex-1 py-2 btn btn-primary">Salvar</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Unidade */}
      {showUnidadeModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-card rounded-lg w-full max-w-2xl p-6 my-8">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">{editingUnidade ? "Editar Unidade" : "Nova Unidade"}</h3>
              <button onClick={closeUnidadeModal}><X className="w-5 h-5" /></button>
            </div>
            <form onSubmit={handleUnidadeSubmit} className="space-y-4 max-h-[70vh] overflow-y-auto pr-2">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">Nome da Unidade *</label>
                  <input type="text" value={unidadeForm.nome} onChange={(e) => setUnidadeForm(p => ({ ...p, nome: e.target.value }))} required className="w-full px-3 py-2 border border-input rounded-lg bg-card text-foreground" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">CNPJ</label>
                  <input type="text" value={unidadeForm.cnpj} onChange={(e) => setUnidadeForm(p => ({ ...p, cnpj: e.target.value }))} className="w-full px-3 py-2 border border-input rounded-lg bg-card text-foreground" />
                </div>
              </div>

              <h4 className="font-medium text-foreground pt-2 border-t">Contato</h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">Nome do Contato</label>
                  <input type="text" value={unidadeForm.contatoNome} onChange={(e) => setUnidadeForm(p => ({ ...p, contatoNome: e.target.value }))} className="w-full px-3 py-2 border border-input rounded-lg bg-card text-foreground" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">Telefone</label>
                  <input type="text" value={unidadeForm.contatoTelefone} onChange={(e) => setUnidadeForm(p => ({ ...p, contatoTelefone: e.target.value }))} className="w-full px-3 py-2 border border-input rounded-lg bg-card text-foreground" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">Email</label>
                  <input type="email" value={unidadeForm.contatoEmail} onChange={(e) => setUnidadeForm(p => ({ ...p, contatoEmail: e.target.value }))} className="w-full px-3 py-2 border border-input rounded-lg bg-card text-foreground" />
                </div>
              </div>

              <h4 className="font-medium text-foreground pt-2 border-t">Endereço</h4>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">CEP</label>
                  <input type="text" value={unidadeForm.cep} onChange={(e) => setUnidadeForm(p => ({ ...p, cep: e.target.value }))} className="w-full px-3 py-2 border border-input rounded-lg bg-card text-foreground" />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-foreground mb-1">Logradouro</label>
                  <input type="text" value={unidadeForm.logradouro} onChange={(e) => setUnidadeForm(p => ({ ...p, logradouro: e.target.value }))} className="w-full px-3 py-2 border border-input rounded-lg bg-card text-foreground" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">Número</label>
                  <input type="text" value={unidadeForm.numero} onChange={(e) => setUnidadeForm(p => ({ ...p, numero: e.target.value }))} className="w-full px-3 py-2 border border-input rounded-lg bg-card text-foreground" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">Complemento</label>
                  <input type="text" value={unidadeForm.complemento} onChange={(e) => setUnidadeForm(p => ({ ...p, complemento: e.target.value }))} className="w-full px-3 py-2 border border-input rounded-lg bg-card text-foreground" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">Bairro</label>
                  <input type="text" value={unidadeForm.bairro} onChange={(e) => setUnidadeForm(p => ({ ...p, bairro: e.target.value }))} className="w-full px-3 py-2 border border-input rounded-lg bg-card text-foreground" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">Cidade</label>
                  <input type="text" value={unidadeForm.cidade} onChange={(e) => setUnidadeForm(p => ({ ...p, cidade: e.target.value }))} className="w-full px-3 py-2 border border-input rounded-lg bg-card text-foreground" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">Estado</label>
                  <input type="text" value={unidadeForm.estado} onChange={(e) => setUnidadeForm(p => ({ ...p, estado: e.target.value }))} maxLength={2} className="w-full px-3 py-2 border border-input rounded-lg bg-card text-foreground" />
                </div>
              </div>

              <div className="flex gap-3 pt-4 border-t">
                <button type="button" onClick={closeUnidadeModal} className="flex-1 py-2 btn btn-outline">Cancelar</button>
                <button type="submit" className="flex-1 py-2 btn btn-primary">Salvar</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Usuário do Cliente */}
      {showUserModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card rounded-lg w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-foreground">{editingUser ? "Editar Usuário" : "Novo Usuário"}</h3>
              <button onClick={closeUserModal}><X className="w-5 h-5" /></button>
            </div>
            <form onSubmit={handleUserSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Nome *</label>
                <input type="text" value={userForm.name} onChange={(e) => setUserForm(p => ({ ...p, name: e.target.value }))} required
                  className="w-full px-3 py-2 border border-input rounded-lg bg-card text-foreground" />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Email *</label>
                <input type="email" value={userForm.email} onChange={(e) => setUserForm(p => ({ ...p, email: e.target.value }))} required
                  className="w-full px-3 py-2 border border-input rounded-lg bg-card text-foreground" />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Senha {editingUser ? "(deixe vazio para manter)" : "*"}</label>
                <input type="password" value={userForm.password} onChange={(e) => setUserForm(p => ({ ...p, password: e.target.value }))} required={!editingUser}
                  className="w-full px-3 py-2 border border-input rounded-lg bg-card text-foreground" />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Perfil</label>
                <select value={userForm.role} onChange={(e) => setUserForm(p => ({ ...p, role: e.target.value }))}
                  className="w-full px-3 py-2 border border-input rounded-lg bg-card text-foreground">
                  {clientRoles.map(role => (
                    <option key={role.value} value={role.value}>{role.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Permissões de Visualização</label>
                <div className="space-y-2 p-3 bg-muted/50 rounded-lg border">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={userForm.permPmoc} onChange={(e) => setUserForm(p => ({ ...p, permPmoc: e.target.checked }))}
                      className="w-4 h-4 text-blue-600 rounded border-input" />
                    <span className="text-sm text-foreground">PMOC (Laudos, Relatórios, Formulários, ARTs)</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={userForm.permNf} onChange={(e) => setUserForm(p => ({ ...p, permNf: e.target.checked }))}
                      className="w-4 h-4 text-blue-600 rounded border-input" />
                    <span className="text-sm text-foreground">Notas Fiscais</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={userForm.permProposta} onChange={(e) => setUserForm(p => ({ ...p, permProposta: e.target.checked }))}
                      className="w-4 h-4 text-blue-600 rounded border-input" />
                    <span className="text-sm text-foreground">Propostas Comerciais</span>
                  </label>
                </div>
                <p className="text-xs text-muted-foreground mt-1">Desmarque para restringir o acesso a determinados tipos de documento</p>
              </div>
              <div className="flex gap-3 pt-4">
                <button type="button" onClick={closeUserModal} className="flex-1 py-2 btn btn-outline">Cancelar</button>
                <button type="submit" className="flex-1 py-2 btn btn-primary">Salvar</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Visualizar Cliente */}
      {viewingClient && (
        <div className="fixed inset-0 bg-black/50 flex items-start justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-card rounded-lg w-full max-w-2xl p-6 my-8">
            {/* Cabeçalho */}
            <div className="flex items-start justify-between mb-5">
              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="text-lg font-semibold">{viewingClient.razaoSocial}</h3>
                  {viewingClient.codigoInterno && (
                    <span className="text-xs bg-muted text-muted-foreground px-2 py-0.5 rounded font-mono">{viewingClient.codigoInterno}</span>
                  )}
                  <span className={`text-xs px-2 py-0.5 rounded-full ${viewingClient.active ? "bg-green-500/20 text-green-700 dark:text-green-400" : "bg-red-500/20 text-red-700 dark:text-red-400"}`}>
                    {viewingClient.active ? "Ativo" : "Inativo"}
                  </span>
                </div>
                {viewingClient.nomeFantasia && (
                  <p className="text-sm text-muted-foreground mt-0.5">{viewingClient.nomeFantasia}</p>
                )}
              </div>
              <button onClick={() => setViewingClient(null)} className="p-1 hover:bg-muted rounded-lg shrink-0 ml-4">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-5">
              {/* Dados da Empresa */}
              <div>
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">Dados da Empresa</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <ViewField label="CNPJ" value={maskCNPJ(viewingClient.cnpj)} />
                  <ViewField label="Segmento" value={viewingClient.segmento} />
                  <ViewField label="Inscrição Estadual" value={viewingClient.inscricaoEstadual} />
                  <ViewField label="Inscrição Municipal" value={viewingClient.inscricaoMunicipal} />
                  <ViewField label="Site" value={viewingClient.site} fullWidth />
                </div>
              </div>

              {/* Contato */}
              <div className="border-t pt-4">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">Contato</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <ViewField label="E-mail" value={viewingClient.email} />
                  <ViewField label="Telefone" value={viewingClient.telefone} />
                  <ViewField label="Celular / WhatsApp" value={viewingClient.celular} />
                  <ViewField label="Responsável" value={viewingClient.contatoNome} />
                  <ViewField label="Telefone do Responsável" value={viewingClient.contatoTelefone} />
                  <ViewField label="E-mail do Responsável" value={viewingClient.contatoEmail} />
                </div>
              </div>

              {/* Endereço */}
              <div className="border-t pt-4">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">Endereço</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <ViewField label="CEP" value={viewingClient.cep ? maskCEP(viewingClient.cep) : null} />
                  <ViewField label="Logradouro" value={viewingClient.logradouro} />
                  <ViewField label="Número" value={viewingClient.numero} />
                  <ViewField label="Complemento" value={viewingClient.complemento} />
                  <ViewField label="Bairro" value={viewingClient.bairro} />
                  <ViewField label="Cidade / UF" value={viewingClient.cidade && viewingClient.estado ? `${viewingClient.cidade} / ${viewingClient.estado}` : viewingClient.cidade || viewingClient.estado} />
                </div>
              </div>

              {/* Observações */}
              {viewingClient.observacoes && (
                <div className="border-t pt-4">
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Observações</p>
                  <p className="text-sm text-foreground whitespace-pre-wrap">{viewingClient.observacoes}</p>
                </div>
              )}

              {/* Contadores */}
              {viewingClient._count && (
                <div className="border-t pt-4 grid grid-cols-4 gap-3 text-center">
                  {[
                    { label: "Unidades", v: viewingClient._count.unidades },
                    { label: "Propostas", v: viewingClient._count.propostas },
                    { label: "Faturas", v: viewingClient._count.faturas },
                    { label: "Docs", v: viewingClient._count.documents },
                  ].map(({ label, v }) => (
                    <div key={label} className="bg-muted/50 rounded-lg p-3">
                      <p className="text-lg font-bold">{v}</p>
                      <p className="text-xs text-muted-foreground">{label}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="flex justify-end gap-3 mt-6 pt-4 border-t">
              <button onClick={() => { setViewingClient(null); handleEdit(viewingClient); }}
                className="flex items-center gap-2 px-4 py-2 btn btn-outline">
                <Edit2 className="w-4 h-4" /> Editar
              </button>
              <button onClick={() => setViewingClient(null)} className="px-4 py-2 btn btn-primary">Fechar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ViewField({ label, value, fullWidth }: { label: string; value: string | null | undefined; fullWidth?: boolean }) {
  return (
    <div className={fullWidth ? "md:col-span-2" : ""}>
      <p className="text-xs text-muted-foreground mb-0.5">{label}</p>
      <p className="text-sm font-medium text-foreground">{value || <span className="text-muted-foreground font-normal">—</span>}</p>
    </div>
  );
}
